'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import { LoadingSection } from '@/app/components/loading/section';
import { LoadingOverlay } from '@/app/components/overlays/loading';
import { CampaignsBigCampaign2026 } from '@/app/lcv/components/campaigns/big-campaign-2026';
import { getTrackerById } from '@/app/lcv/components/campaigns/big-campaign-2026/actions/getTrackerById';
import { CAMPAIGN_ID, type CampaignModel, type TrackerResult } from '@/app/lcv/components/campaigns/big-campaign-2026/actions/schema';
import { buildTrace, sanitizeSearch, toTraceUrlParams } from '@/app/lcv/components/campaigns/big-campaign-2026/shared/trace';
import { traceStep } from '@/app/lcv/components/campaigns/big-campaign-2026/shared/trace-client';
import type { DealerInformationType, QueryParamsType } from '@/app/lcv/components/campaigns/big-campaign-2026/types';
import type { ProvinceWithDistrictsType } from '@/app/types/form';
interface RegisterEntryProps {
  provinces: ProvinceWithDistrictsType[];
  branchCodeData: Record<string, DealerInformationType>;
  models: CampaignModel[];
  formName: string;
}
const EMPTY_QUERY_PARAMS: QueryParamsType = {};
const readQueryParams = (params: Pick<URLSearchParams, 'get'> | null): QueryParamsType => ({
  scId: params?.get('scId') ?? '',
  branchCode: params?.get('branchCode') ?? '',
  model: params?.get('model') ?? '',
  modelCode: params?.get('modelCode') ?? '',
  refTrackerId: params?.get('refTrackerId') ?? '',
  leadId: params?.get('leadId') ?? ''
});
const CampaignWithUrlParams = props => {
  const $ = _c(14);
  const t0 = useSearchParams();
  let t1;
  if ($[0] !== t0) {
    t1 = readQueryParams(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  const {
    refTrackerId: t2,
    modelCode: t3
  } = queryParams;
  const refTrackerId = t2 === undefined ? "" : t2;
  const modelCode = t3 === undefined ? "" : t3;
  const modelsCount = props.models.length;
  const [tracker, setTracker] = useState();
  let t4;
  let t5;
  if ($[2] !== modelsCount) {
    t4 = () => {
      const search = window.location.search;
      const urlParams = new URLSearchParams(search);
      traceStep("pageLoad", {
        ...toTraceUrlParams(search),
        search: sanitizeSearch(search),
        modelCodeParam: urlParams.get("modelCode"),
        refTrackerId: urlParams.get("refTrackerId"),
        source: urlParams.get("source"),
        referrer: document.referrer.slice(0, 300),
        userAgent: navigator.userAgent.slice(0, 300),
        isLineUA: /\bLine\//i.test(navigator.userAgent),
        modelsCount
      });
    };
    t5 = [modelsCount];
    $[2] = modelsCount;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[5] !== modelCode || $[6] !== refTrackerId) {
    t6 = () => {
      if (!refTrackerId) {
        return;
      }
      let isActive = true;
      getTrackerById({
        trackerId: refTrackerId,
        campaign: CAMPAIGN_ID,
        modelCode: modelCode || undefined
      }, buildTrace("trackerLookup", {
        modelCodeParam: modelCode
      })).then(_temp).catch(_temp2).then(data => {
        if (isActive) {
          setTracker({
            id: refTrackerId,
            data
          });
        }
      });
      return () => {
        isActive = false;
      };
    };
    t7 = [refTrackerId, modelCode];
    $[5] = modelCode;
    $[6] = refTrackerId;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  useEffect(t6, t7);
  if (refTrackerId && tracker?.id !== refTrackerId) {
    let t8;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <LoadingOverlay show={true}><LoadingSection /></LoadingOverlay>;
      $[9] = t8;
    } else {
      t8 = $[9];
    }
    return t8;
  }
  const t8 = tracker?.data ?? null;
  let t9;
  if ($[10] !== props || $[11] !== queryParams || $[12] !== t8) {
    t9 = <CampaignsBigCampaign2026 {...props} queryParams={queryParams} trackerData={t8} />;
    $[10] = props;
    $[11] = queryParams;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
};

/** The campaign on /register (static): reads its URL params in the browser. */
export const RegisterEntry = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Suspense fallback={<CampaignsBigCampaign2026 {...props} queryParams={EMPTY_QUERY_PARAMS} />}><CampaignWithUrlParams {...props} /></Suspense>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
function _temp(result) {
  return result.data ?? null;
}
function _temp2() {
  return null;
}